import React, { useState } from "react";
import Text from "components/common/Text";
import Link from "components/common/Link";
import Stack from "components/common/Stack";
import Box from "components/common/Box";
import Button from "components/common/Button";
import { config } from "config";
import SocialLogin from "containers/SocialLogin";
import Divider from "@mui/material/Divider";
import OtpInput from "react-otp-input";
import AuthTextField from "components/Auth/shared/AuthTextField";
import AuthPhoneField from "components/Auth/shared/AuthPhoneField";
import AuthPrimaryButton from "components/Auth/shared/AuthPrimaryButton";
import AuthModeToggle from "components/Auth/shared/AuthModeToggle";
import { useAuthFieldStyles } from "components/Auth/shared/authFieldStyles";

const LoginTerms = () => (
  <Box
    sx={{
      mb: "12px",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
    }}
  >
    <Text color="#747474" variant="small" sx={{ fontSize: "12px" }}>
      By continuing, I agree to the{" "}
      <a href={config.disclaimerUrl} target="_blank" rel="noreferrer">
        JARVIS Disclaimer
      </a>{" "}
      and{" "}
      <a href={config.tacUrl} target="_blank" rel="noreferrer">
        T&amp;Cs
      </a>
      .
    </Text>
  </Box>
);

const LoginForm = ({
  forgotPwdUrl,
  onSubmit,
  onConfirmOTP,
  onChange,
  isLoading = false,
  formError,
  formData,
  loginSendOtp,
  isLoginRequestOtp,
  loginOtpResendTime,
  onBackToStep1,
  viewTypes,
  onChangeViewType,
  activeViewType,
  otpInputMode = "phone",
  onChangeOtpInputMode,
}) => {
  const classes = useAuthFieldStyles();
  const [localOtpMode, setLocalOtpMode] = useState("phone");

  const mode = onChangeOtpInputMode ? otpInputMode : localOtpMode;
  const setMode = (next) => {
    if (onChangeOtpInputMode) {
      onChangeOtpInputMode(next);
    } else {
      setLocalOtpMode(next);
      onChange("userName", "");
    }
  };

  const isLoginWithPassword = activeViewType === viewTypes.LOGIN_WITH_PASSWORD;
  const isPhoneMode = mode === "phone";

  const onEnterSendOtp = (e) => {
    if (e.keyCode === 13 || e.which === 13) {
      loginSendOtp({ isFirstRequest: true });
    }
  };

  const switchOtpMode = (next) => {
    setMode(next);
    onChange("userName", "");
  };

  return (
    <Stack
      alignItems="center"
      sx={{
        py: { xs: 3, md: 2 },
        px: { xs: 2.5, md: 0 },
        width: "100%",
        minHeight: "100%",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      {config.environment === "DEVELOPMENT" && (
        <Text sx={{ color: "#ff6262", fontSize: "14px", fontWeight: 500, mb: 1 }}>
          This is {config.environment} app for {config.platform}
        </Text>
      )}

      <Stack
        sx={{
          width: { xs: "100%", md: 546 },
          maxWidth: "100%",
        }}
        alignItems="stretch"
        justifyContent="flex-start"
      >
        {/* Header */}
        {!isLoginRequestOtp ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 3, mt: { xs: 1, md: 2 }, width: "100%" }}
          >
            <Text sx={{ fontSize: "22px", fontWeight: 500 }}>Sign In</Text>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                onChangeViewType(
                  isLoginWithPassword
                    ? viewTypes.LOGIN_WITH_OTP
                    : viewTypes.LOGIN_WITH_PASSWORD,
                );
              }}
              sx={{
                color: "#2977ec",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                minWidth: "auto",
                p: 0,
                background: "transparent !important",
                boxShadow: "none !important",
              }}
            >
              Login with {isLoginWithPassword ? "OTP" : "password"}
            </Button>
          </Stack>
        ) : (
          <Text
            sx={{
              fontSize: "22px",
              fontWeight: 500,
              mb: 3,
              mt: { xs: 1, md: 2 },
            }}
          >
            OTP Verification
          </Text>
        )}

        {isLoginWithPassword ? (
          <>
            <AuthTextField
              label="Email ID"
              type="email"
              placeholder="you@gmail.com"
              value={formData.email}
              onChange={(e) => onChange("email", e.target.value)}
              error={formError.email}
              onKeyUp={onEnterSendOtp}
            />
            <AuthTextField
              label="Password"
              type="password"
              placeholder="Enter password"
              value={formData.password}
              onChange={(e) => onChange("password", e.target.value)}
              error={formError.password}
            />
            <Stack alignItems="flex-end" sx={{ width: "100%", mb: 2, mt: -2 }}>
              <Link
                underline="none"
                href={forgotPwdUrl}
                sx={{ color: "#2977ec", fontSize: "12px", fontWeight: 500 }}
              >
                Forgot Password?
              </Link>
            </Stack>
            <LoginTerms />
            <AuthPrimaryButton
              onClick={onSubmit}
              loading={isLoading}
              showArrow
              sx={{ mb: 2 }}
            >
              Login
            </AuthPrimaryButton>
            {config.platform !== "IOS" && (
              <>
                <Box sx={{ width: "100%", mb: 3 }}>
                  <Divider />
                </Box>
                <SocialLogin />
              </>
            )}
          </>
        ) : (
          <>
            {!isLoginRequestOtp ? (
              <>
                <AuthModeToggle
                  value={mode}
                  onChange={(next) => switchOtpMode(next)}
                />

                {isPhoneMode ? (
                  <AuthPhoneField
                    label=""
                    value={formData.userName}
                    onChange={(digits) => onChange("userName", digits)}
                    error={formError.userName}
                    onKeyUp={onEnterSendOtp}
                  />
                ) : (
                  <AuthTextField
                    label=""
                    type="email"
                    placeholder="you@gmail.com"
                    value={formData.userName}
                    onChange={(e) => onChange("userName", e.target.value)}
                    error={formError.userName}
                    onKeyUp={onEnterSendOtp}
                  />
                )}

                <LoginTerms />

                <AuthPrimaryButton
                  onClick={() => loginSendOtp({ isFirstRequest: true })}
                  loading={isLoading}
                  showArrow
                  sx={{ mb: 2 }}
                >
                  Login
                </AuthPrimaryButton>

                <Text
                  sx={{
                    textAlign: "center",
                    fontSize: "14px",
                    color: "#000",
                  }}
                >
                  Don&apos;t have an account?{" "}
                  <Link
                    href="/signup"
                    sx={{
                      color: "#2977ec",
                      fontWeight: 500,
                      textDecoration: "underline",
                      fontSize: "14px",
                    }}
                  >
                    Sign up
                  </Link>
                </Text>
              </>
            ) : (
              <Stack sx={{ width: 1, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Text
                    variant="caption1"
                    sx={{ wordBreak: "break-word", marginRight: "10px" }}
                  >
                    Enter OTP sent on {formData.userName}
                  </Text>
                  <Button
                    component="span"
                    className={classes.otpResend}
                    sx={{ fontSize: "15px" }}
                    onClick={onBackToStep1}
                    disabled={loginOtpResendTime > 0}
                    loading={isLoading}
                  >
                    Edit
                  </Button>
                </Stack>

                <OtpInput
                  className={classes.otpContainer}
                  inputStyle={classes.otpInput}
                  value={formData.loginOtp}
                  numInputs={6}
                  isInputNum={true}
                  shouldAutoFocus={true}
                  onChange={(e) => onChange("loginOtp", e)}
                  separator={<span>&nbsp;</span>}
                />

                <Stack
                  sx={{
                    whiteSpace: "nowrap",
                    alignSelf: "flex-end",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    flexFlow: "row wrap",
                    fontSize: "15px",
                  }}
                >
                  <Button
                    component="span"
                    className={classes.otpResend}
                    sx={{ fontSize: "15px" }}
                    onClick={() => loginSendOtp()}
                    disabled={loginOtpResendTime > 0}
                    loading={isLoading}
                  >
                    Resend {loginOtpResendTime <= 0 && "OTP"}
                  </Button>
                  {loginOtpResendTime > 0 ? (
                    <Text variant="caption1" sx={{ wordBreak: "break-word" }}>
                      in 00:
                      {loginOtpResendTime < 10
                        ? `0${loginOtpResendTime}`
                        : loginOtpResendTime}
                    </Text>
                  ) : null}
                </Stack>

                <AuthPrimaryButton
                  onClick={onConfirmOTP}
                  loading={isLoading}
                  disabled={formData.loginOtp.length !== 6}
                  sx={{ mt: 2, mb: 2 }}
                >
                  Confirm OTP
                </AuthPrimaryButton>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default LoginForm;
